import { FormattedNumber } from 'react-intl';

import { getPublicOfferPriceEndDecorator } from '»utils/endDecorator';
import { TrlFormattedMessagePlain } from '»utils/i18n';
import { displayNominalValue } from '»utils/nominalValue';
import { getDecimalDigits } from '»utils/number';

import { DataValue } from '../../components/molecules/TrlCard/TrlCardStockExchangeDet.styles';
import { formatPrimaryTitlePrice, getPriceTypeLabel } from '../price';
import { useCurrentPage } from './useCurrentPage';

export function useTitleDetails(titleData) {
  const { isMarketPrimaryPage, isFundsListPage } = useCurrentPage();

  const decimalDigits = getDecimalDigits(titleData.roundPrice);

  if (isMarketPrimaryPage)
    return {
      detailsContainerId: 'divPublicOfferDetailWrapperMobile',
      details: [
        {
          values: titleData.settlementDate,
          label: TrlFormattedMessagePlain({
            id: 'TRLT_UI-LIQUIDATION',
          }),
          type: 'date',
          style: { gridArea: 'liquidacao' },
        },

        {
          values: titleData.subscriptionDate,
          label: TrlFormattedMessagePlain({
            id: 'TRLT_UI-TRADING_ADMISSION',
          }),
          type: 'date',
          style: { gridArea: 'aNegativa' },
        },
        {
          values: titleData.currency,
          label: TrlFormattedMessagePlain({ id: 'TRLT_UI-CURRENCY' }),
          style: { gridArea: 'moeda' },
        },

        {
          values:
            titleData.quantityType === 'QTY'
              ? TrlFormattedMessagePlain({ id: 'TRLT_UI-QUANTITY' })
              : TrlFormattedMessagePlain({ id: 'TRLT_UI-AMOUNT' }),

          label: TrlFormattedMessagePlain({
            id: 'TRLT_UI-QUANTITY_TYPE',
          }),
          style: { gridArea: 'tQuantidade' },
        },
        {
          values:
            titleData.trancheType === 'R'
              ? titleData.minAmount
              : titleData.minQuantity,
          label: TrlFormattedMessagePlain({
            id:
              titleData.trancheType === 'R'
                ? 'TRLT_UI-MINIMUM_AMOUNT'
                : 'TRLT_UI-MINIMUM_QTY',
          }),
          type: 'number',
          style: { gridArea: 'qMinima' },
        },
        {
          values:
            titleData.trancheType === 'R'
              ? titleData.maxAmount
              : titleData.maxQuantity,
          label: TrlFormattedMessagePlain({
            id:
              titleData.trancheType === 'R'
                ? 'TRLT_UI-MAX_AMOUNT'
                : 'TRLT_UI-MAXIMUM_QTY',
          }),
          type: 'number',
          style: { gridArea: 'qMaxima' },
        },
        {
          values: titleData.minimumBatch,
          label: TrlFormattedMessagePlain({
            id: 'TRLT_UI-QTY_MULTIPLES',
          }),
          type: 'number',
          style: { gridArea: 'qMultiplos' },
        },
        {
          endDecorator: getPublicOfferPriceEndDecorator(
            titleData.priceType,
            titleData.typeNegotiation,
          ),
          values: titleData.price,
          label: getPriceTypeLabel({ value: titleData.priceType }),
          type: 'number',
          decimalDigits,
          style: { gridArea: 'preco' },
        },
        {
          values: formatPrimaryTitlePrice({
            trancheType: titleData.trancheType,
            priceType: titleData.priceType,
            price: titleData.price,
            minMaxPrice: titleData.minPrice,
            offerPrice: titleData.offerMinPrice,
          }),
          label: TrlFormattedMessagePlain({
            id: `${
              titleData.priceType === 'T'
                ? 'TRLT_UI-MIN_INTEREST_RATE'
                : 'TRLT_UI-MINIMUM_PRICE'
            }`,
          }),
          style: { gridArea: 'pMinimo' },
        },
        {
          values: formatPrimaryTitlePrice({
            trancheType: titleData.trancheType,
            priceType: titleData.priceType,
            price: titleData.price,
            minMaxPrice: titleData.maxPrice,
            offerPrice: titleData.offerMaxPrice,
          }),
          label: TrlFormattedMessagePlain({
            id: `${
              titleData.priceType === 'T'
                ? 'TRLT_UI-MAX_INTEREST_RATE'
                : 'TRLT_UI-MAXIMUM_PRICE'
            }`,
          }),
          style: { gridArea: 'pMaximo' },
        },
        {
          isDocument: true,
          label: TrlFormattedMessagePlain({
            id: 'TRLT_UI-DOCUMENTS',
          }),
          noteId: titleData.noteId,
          style: { gridArea: 'documentos' },
        },
      ],
      datesContainerId: 'divStockDataPublicOffer',
      dates: [
        [
          {
            label: 'TRLT_UI-SUBSCRIPTION',
            date: titleData.operationDate,
            hasTime: true,
          },
          {
            label: 'TRLT_UI-MODIFICATION_LIMIT',
            date: titleData.revokeDate,
            hasTime: true,
          },
        ],
        [
          {
            label: 'TRLT_UI-START_INTENTION',
            date: titleData.startDate,
            hasTime: true,
          },
          {
            label: 'TRLT_UI-END_INTENTION',
            date: titleData.endDate,
            hasTime: true,
          },
        ],
      ],
    };

  if (isFundsListPage)
    return {
      detailsContainerId: 'divFundsPrimaryDetailWrapperMobile',
      details: [
        {
          values: titleData.fundManager,
          label: TrlFormattedMessagePlain({
            id: 'TRLT_UI-MANAGEMENT_COMPANY',
          }),
          style: { gridArea: 'foundManager' },
        },
        {
          values: titleData.initialAmount,
          label: TrlFormattedMessagePlain({
            id: 'TRLT_UI-INITIAL_AMOUNT',
          }),
          style: { gridArea: 'initialAmount' },
        },
        {
          values: titleData.issuerEntityDescription,
          label: TrlFormattedMessagePlain({
            id: 'TRLT_UI-ISSUER',
          }),
          style: { gridArea: 'emitente' },
        },
        {
          values: titleData.symbol,
          label: TrlFormattedMessagePlain({
            id: 'TRLT_UI-TICKER',
          }),
          style: { gridArea: 'symbol' },
        },
        {
          isDocument: true,
          label: TrlFormattedMessagePlain({
            id: 'TRLT_UI-DOCUMENTS',
          }),
          noteId: titleData.noteId,
          style: { gridArea: 'documentos' },
        },
      ],
      datesContainerId: 'divFundsDataPublicOffer',
      dates: [
        [
          {
            label: 'TRLT_UI-SUBSCRIPTION_DATE',
            date: titleData.subscriptionDate,
            hasTime: false,
          },
          {
            label: 'TRLT_UI-MODIFICATION_LIMIT',
            date: titleData.revokeDate,
            hasTime: false,
          },
        ],
        [
          {
            label: 'TRLT_UI-START_INTENTION',
            date: titleData.startDate,
            hasTime: false,
          },
          {
            label: 'TRLT_UI-END_INTENTION',
            date: titleData.endDate,
            hasTime: false,
          },
        ],
      ],
    };

  return {
    detailsContainerId: 'divPortfolioDetailWrapperMobile',
    details: [
      {
        label: TrlFormattedMessagePlain({ id: 'TRLT_UI-AVAILABLE_QTY' }),
        values: titleData.availableQuantity,
        type: 'number',
        style: { gridArea: 'qntDisponivel' },
      },
      {
        label: TrlFormattedMessagePlain({ id: 'TRLT_UI-NOMINAL_VALUE' }),
        values: displayNominalValue(
          titleData.quantityRepresentation,
          titleData.priceRepresentation,
          titleData.nominalValue,
        ),
        type: 'number',
        decimalDigits,
        style: { gridArea: 'vNominal' },
      },
      {
        label: TrlFormattedMessagePlain({
          id: 'TRLT_UI-ACQUISITION_VALUE',
        }),
        values: titleData.acquisitionValue,
        type: 'number',
        decimalDigits,
        style: { gridArea: 'vAquisicao' },
      },
      {
        label: TrlFormattedMessagePlain({ id: 'TRLT_UI-VALUE_AOA' }),
        values: titleData.currentValueBase,
        type: 'number',
        decimalDigits,
        style: { gridArea: 'vAtualMoeda' },
      },
      {
        label: TrlFormattedMessagePlain({ id: 'TRLT_UI-DAILY_VARIATION' }),
        values: (
          <DataValue value={titleData.variationAmountBase} isOrder>
            <FormattedNumber
              value={titleData.variationAmountBase}
              maximumFractionDigits={decimalDigits}
              minimumFractionDigits={decimalDigits}
            />
          </DataValue>
        ),
        style: { gridArea: 'vDiaria' },
      },
      {
        label: TrlFormattedMessagePlain({ id: 'TRLT_UI-PORTFOLIO_PERC' }),
        values: titleData.portfolioPctg,
        decimalDigits,
        endDecorator: '%',
        type: 'number',
        style: { gridArea: 'carteira' },
      },
    ],
  };
}
